/** El árbol de páginas de la barra lateral: plegable, con acciones al pasar el ratón y con
 * arrastrar y soltar para reordenar o anidar, como en Notion.
 *
 * Soltar sobre el **tercio de arriba** de una fila coloca antes, sobre el **de abajo** después, y
 * sobre el **del medio** la mete dentro como subpágina. La posición es fraccionaria (la media de
 * sus vecinas), así que mover una página es un único PATCH y no renumera nada.
 */
import { useRef, useState } from 'react'
import { NavLink, useParams } from 'react-router-dom'

import { usePortal } from '../contexto'
import { useEspacio } from './contexto'
import { Menu, type Opcion } from './Menu'
import type { Nodo } from './tipos'

type Donde = 'antes' | 'dentro' | 'despues'

export function IconoDePagina({ icono, tam = 18, tipo }: {
  icono: string | null; tam?: number; tipo?: string
}) {
  if (icono) return <span className="pag-icono" style={{ fontSize: tam - 3 }}>{icono}</span>
  if (tipo === 'database') {
    return (
      <svg className="pag-icono pag-icono--vacio" width={tam} height={tam} viewBox="0 0 20 20"
           aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.4">
        <rect x="2.5" y="3.5" width="15" height="13" rx="1.5" />
        <path d="M2.5 8h15M8 8v8.5" />
      </svg>
    )
  }
  return (
    <svg className="pag-icono pag-icono--vacio" width={tam} height={tam} viewBox="0 0 20 20"
         aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.4">
      <path d="M5.5 2.5h6l3.5 3.5v10.5a1 1 0 0 1-1 1h-8.5a1 1 0 0 1-1-1v-13a1 1 0 0 1 1-1Z" />
      <path d="M11.5 2.5V6H15" />
    </svg>
  )
}

export function ListaDePaginas({ padre, nivel }: { padre: number | null; nivel: number }) {
  const { hijos } = useEspacio()
  const lista = hijos(padre)
  if (lista.length === 0) return null
  return (
    <div role="group">
      {lista.map((n) => (
        <Fila key={n.id} nodo={n} nivel={nivel} />
      ))}
    </div>
  )
}

/** Quién cuelga de quién, para no soltar una página sobre sus propias descendientes. */
function esDescendiente(arbol: Nodo[], posible: number, de: number): boolean {
  let actual: number | null = posible
  const vistos = new Set<number>()
  while (actual != null && !vistos.has(actual)) {
    if (actual === de) return true
    vistos.add(actual)
    actual = arbol.find((n) => n.id === actual)?.parent_id ?? null
  }
  return false
}

// Qué página se está arrastrando. Es de módulo y no de estado: el navegador no deja leer los
// datos del arrastre hasta soltar, y hay que saberlo mientras se pasa por encima.
let arrastrando: number | null = null

function Fila({ nodo, nivel }: { nodo: Nodo; nivel: number }) {
  const { servidor, puedo } = usePortal()
  const {
    arbol, hijos, expandidos, alternarExpandido, crear, mover, alternarFavorita, aLaPapelera,
    duplicar,
  } = useEspacio()
  const { id: idActual } = useParams()
  const base = `/${servidor.id}/paginas/${nodo.id}`
  const puedeEditar = puedo('editar_paginas')

  const tiene = hijos(nodo.id).length > 0
  const abierto = expandidos.has(nodo.id)
  const [donde, setDonde] = useState<Donde | null>(null)
  const [menu, setMenu] = useState(false)
  const botonMenu = useRef<HTMLButtonElement>(null)
  const fila = useRef<HTMLDivElement>(null)

  const zona = (e: React.DragEvent): Donde => {
    const r = fila.current!.getBoundingClientRect()
    const y = (e.clientY - r.top) / r.height
    return y < 0.28 ? 'antes' : y > 0.72 ? 'despues' : 'dentro'
  }

  const soltar = (e: React.DragEvent) => {
    e.preventDefault()
    const origen = arrastrando
    const sitio = zona(e)
    setDonde(null)
    arrastrando = null
    if (origen == null || origen === nodo.id) return

    if (sitio === 'dentro') {
      void mover(origen, nodo.id)
      return
    }
    // Entre dos hermanas: la media de las posiciones; en un extremo, un paso más allá.
    const hermanas = hijos(nodo.parent_id).filter((h) => h.id !== origen)
    const i = hermanas.findIndex((h) => h.id === nodo.id)
    const vecina = sitio === 'antes' ? hermanas[i - 1] : hermanas[i + 1]
    const mia = nodo.position
    const otra = vecina?.position ?? (sitio === 'antes' ? mia - 2048 : mia + 2048)
    void mover(origen, nodo.parent_id, (mia + otra) / 2)
  }

  const opciones: Opcion[] = [
    {
      texto: nodo.favorita ? 'Quitar de favoritos' : 'Añadir a favoritos',
      icono: nodo.favorita ? '★' : '☆',
      alElegir: () => void alternarFavorita(nodo.id),
    },
    ...(puedeEditar
      ? ([
          { texto: 'Duplicar', icono: '⧉', alElegir: () => void duplicar(nodo.id) },
          ...(nodo.kind === 'page'
            ? [{
                texto: 'Añadir subpágina',
                icono: '＋',
                alElegir: () => void crear({ parent_id: nodo.id }),
              }]
            : []),
          { separador: true, texto: '' },
          {
            texto: 'Mover a la papelera',
            icono: '🗑',
            peligro: true,
            alElegir: () => void aLaPapelera(nodo.id),
          },
        ] as Opcion[])
      : []),
  ]

  return (
    <div role="treeitem" aria-expanded={tiene ? abierto : undefined}>
      <div
        ref={fila}
        className={`arbol-fila${donde ? ` arbol-fila--${donde}` : ''}`}
        style={{ paddingLeft: 6 + nivel * 14 }}
        draggable={puedeEditar}
        onDragStart={(e) => {
          arrastrando = nodo.id
          e.dataTransfer.effectAllowed = 'move'
          e.dataTransfer.setData('text/plain', String(nodo.id))
        }}
        onDragEnd={() => {
          arrastrando = null
          setDonde(null)
        }}
        onDragOver={(e) => {
          if (arrastrando == null || arrastrando === nodo.id) return
          if (esDescendiente(arbol, nodo.id, arrastrando)) return
          e.preventDefault()
          setDonde(zona(e))
        }}
        onDragLeave={() => setDonde(null)}
        onDrop={(e) => {
          if (arrastrando != null && esDescendiente(arbol, nodo.id, arrastrando)) return
          soltar(e)
        }}
      >
        <button
          className="arbol-caret"
          aria-label={abierto ? 'Plegar' : 'Desplegar'}
          onClick={() => alternarExpandido(nodo.id)}
          style={{ visibility: tiene || puedeEditar ? 'visible' : 'hidden' }}
          tabIndex={-1}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"
               style={{ transform: abierto ? 'rotate(90deg)' : 'none' }}>
            <path d="M4 2.5 8 6l-4 3.5" fill="none" stroke="currentColor" strokeWidth="1.6"
                  strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <NavLink
          to={base}
          className={({ isActive }) => `arbol-enlace${isActive ? ' arbol-enlace--activo' : ''}`}
          aria-current={String(nodo.id) === idActual ? 'page' : undefined}
        >
          <IconoDePagina icono={nodo.icon} tipo={nodo.kind} />
          <span className="arbol-titulo">{nodo.title || 'Sin título'}</span>
          {nodo.restringida && <span className="arbol-candado" title="Acceso restringido" aria-label="Acceso restringido">🔒</span>}
        </NavLink>

        <span className="arbol-acciones">
          <button
            ref={botonMenu}
            className="arbol-boton"
            aria-label="Más acciones"
            onClick={() => setMenu((v) => !v)}
          >
            ⋯
          </button>
          {puedeEditar && nodo.kind === 'page' && (
            <button
              className="arbol-boton"
              aria-label="Añadir subpágina"
              onClick={() => void crear({ parent_id: nodo.id })}
            >
              ＋
            </button>
          )}
        </span>
        {menu && (
          <Menu ancla={botonMenu} opciones={opciones} alCerrar={() => setMenu(false)} ancho={230} />
        )}
      </div>

      {abierto &&
        (tiene ? (
          <ListaDePaginas padre={nodo.id} nivel={nivel + 1} />
        ) : (
          <div className="arbol-vacio" style={{ paddingLeft: 30 + nivel * 14 }}>
            Sin páginas dentro
          </div>
        ))}
    </div>
  )
}

/** Un acceso directo a una favorita, plano y sin hijos. */
export function FilaDeFavorita({ nodo }: { nodo: Nodo }) {
  const { servidor } = usePortal()
  return (
    <NavLink
      to={`/${servidor.id}/paginas/${nodo.id}`}
      className={({ isActive }) => `arbol-enlace arbol-enlace--suelto${isActive ? ' arbol-enlace--activo' : ''}`}
    >
      <IconoDePagina icono={nodo.icon} tipo={nodo.kind} />
      <span className="arbol-titulo">{nodo.title || 'Sin título'}</span>
    </NavLink>
  )
}
